import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  createFileRoute,
  useLocation,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router'
import { useState } from 'react'
import { Building2, Loader2, ArrowRight } from 'lucide-react'
import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'

// Type definition for tenant information
type TenantInfo = {
  id: string
  name: string
  description: string
}

type SelectTenantSearch = {
  redirectTo?: string
}

export const Route = createFileRoute('/_authenticated/select-tenant')({
  component: SelectTenantPage,
  validateSearch(search: Record<string, unknown>): SelectTenantSearch {
    return {
      redirectTo: search['redirectTo'] as string | undefined,
    }
  },
})

function SelectTenantPage() {
  const routeContext = useRouteContext({ from: '/_authenticated' })
  const { availableTenants } = routeContext
  const location = useLocation()

  // Sort tenants alphabetically by name (first character ranking)
  const sortedTenants = availableTenants
    ?.slice()
    .sort((a, b) => a.name.localeCompare(b.name))
  const navigate = useNavigate()
  const [selectedTenant, setSelectedTenant] = useState<string>('')
  const [isNavigating, setIsNavigating] = useState(false)
  const setSelectedTenantId = useSelectedTenantStore(
    (state) => state.setSelectedTenantId,
  )

  const handleTenantSelection = async (tenantId: string) => {
    setIsNavigating(true)

    try {
      // Store the selected tenant in localStorage
      setSelectedTenantId(tenantId)

      await navigate({ to: location.search.redirectTo ?? '/' })
    } catch (error) {
      console.error('Error navigating:', error)
      setIsNavigating(false)
    }
  }

  if (!availableTenants || availableTenants.length === 0) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>No Organizations Available</CardTitle>
            <CardDescription>
              You don't have access to any organizations.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-screen w-full items-center justify-center bg-gray-50">
      <Card className="w-96">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <Building2 className="text-primary h-12 w-12" />
          </div>
          <CardTitle>Select Organization</CardTitle>
          <CardDescription>
            You have access to multiple organizations. Please select which one
            you'd like to access.
            <br />
            <span className="text-muted-foreground mt-1 block text-xs">
              Tip: Double-click an organization to select and continue
              immediately.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 h-80 w-full overflow-y-auto pr-2">
            <div className="space-y-3">
              {sortedTenants?.map((tenant: TenantInfo) => (
                <Button
                  key={tenant.id}
                  variant={selectedTenant === tenant.id ? 'default' : 'outline'}
                  className="h-auto w-full justify-between p-4 text-left"
                  onClick={() => setSelectedTenant(tenant.id)}
                  onDoubleClick={() => handleTenantSelection(tenant.id)}
                  disabled={isNavigating}
                >
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{tenant.name}</span>
                    {tenant.description && (
                      <span className="text-sm">{tenant.description}</span>
                    )}
                  </div>
                  {selectedTenant === tenant.id && (
                    <ArrowRight className="ml-2 h-4 w-4" />
                  )}
                </Button>
              ))}
            </div>
          </div>

          <Button
            className="mt-6 w-full"
            onClick={() => handleTenantSelection(selectedTenant)}
            disabled={selectedTenant !== '' && isNavigating}
          >
            {isNavigating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                Continue to Organization
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
